<template>
    <div class="survey-rule">
        <resource-select
            :value="survey"
            :options="surveys"
            :loading="loadingResources"
            :read-only="readOnly"
            field="surveyId"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('surveyRule.Select survey')"
            @input="onSurveyChange">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="survey">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                :options="surveyQuestions"
                :value="selectedSurveyQuestion"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                preselect-first
                field="surveyQuestionId"
                :placeholder="$t('surveyRule.Select survey question')"
                @input="onSurveyQuestionChange" />
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Criteria') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                :options="surveyOperators"
                :value="operatorValue"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                preselect-first
                field="operator"
                @input="$emit('input', $event)" />
            <template v-if="showValueComponent">
                <component
                    :is="valueComponent.component"
                    v-bind="valueComponent.props"
                    :read-only="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="onAnswerChange" />
            </template>
            <div
                v-if="!isLocaleEnglish"
                class="flex-break" />
            <simple-select
                :options="timeOptions"
                :value="timeValue"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                preselect-first
                field="time"
                @input="$emit('input', $event)" />
            <component
                :is="dateComponent.component"
                v-if="dateComponent"
                v-bind="dateComponent.props"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
        </template>
    </div>
</template>

<script>
import { getNoResourcesFoundText } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import StringInput from '@/segments/components/segment-editor-vue/components/string-input.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import SurveyItemsAggregation from '@/surveys/aggregations/survey-items/survey-items.aggregation';
import { runAggregationOnce } from '@/utils/aggregation';
import { SURVEY_QUESTION_TYPES } from '@/surveys/constants/surveys.constants.js';

import {
    getTimeOptions,
    getStringConfig,
    getRelativeDateRangeConfig,
    getAbsoluteDateRangeConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    name: 'SurveyRule',
    components: {
        AbsoluteDateRange,
        NumberInput,
        RelativeDateRange,
        ResourceSelect,
        SimpleSelect,
        SingleDate,
        StringInput,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            timeOptions: [],
            surveyQuestions: [],
        };
    },
    computed: {
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp, resourceType: 'survey' });
        },
        surveys () {
            return this.resources
                .map((resource) => {
                    const guide = resource.delivery[0];

                    return {
                        surveyId: resource.id,
                        name: resource.name,
                        guideId: guide.deliveryId,
                        surveyItemIds: guide.steps ? guide.steps.flatMap((step) => step.surveyItemIds || []) : [],
                        state: resource.status,
                    };
                });
        },
        survey () {
            if (!this.rule.surveyId) return;

            return this.surveys.find((survey) => survey.surveyId === this.rule.surveyId);
        },
        selectedSurveyQuestion () {
            return this.surveyQuestions.find((question) => question.surveyQuestionId === this.rule.surveyQuestionId);
        },
        surveyOperators () {
            if (!this.selectedSurveyQuestion) return [];

            let options;
            const equalToOptions = [
                { operator: '==', name: this.$t('surveyRule.is equal to') },
                { operator: '!=', name: this.$t('surveyRule.is not equal to') },
            ];

            switch (this.selectedSurveyQuestion.surveyQuestionType) {
                case SURVEY_QUESTION_TYPES.OPEN_ANSWER:
                    options = [
                        { operator: 'contains', name: this.$t('surveyRule.contains') },
                        { operator: '!contains', name: this.$t('surveyRule.does not contain') },
                    ];
                    break;
                case SURVEY_QUESTION_TYPES.RATING_SCALE:
                    options = [
                        { operator: '<=', name: this.$t('surveyRule.less or equal to') },
                        { operator: '>=', name: this.$t('surveyRule.greater or equal to') },
                    ];
                    break;
                default:
                    options = [];
                    break;
            }

            return [...equalToOptions, ...options];
        },
        operatorValue () {
            return this.surveyOperators.find((operator) => operator.operator === this.rule.operator);
        },
        answerValues () {
            return this.selectedSurveyQuestion?.answers;
        },
        showValueComponent () {
            return !!this.rule.operator && !!this.selectedSurveyQuestion;
        },
        valueComponent () {
            if (this.selectedSurveyQuestion.surveyQuestionType === SURVEY_QUESTION_TYPES.OPEN_ANSWER) {
                return getStringConfig(this.rule, 'value');
            }

            return {
                component: 'simple-select',
                props: {
                    value: this.answerValue,
                    field: 'answerId',
                    options: this.answerValues,
                    preselectFirst: true,
                    searchable: false,
                    disabled: this.readOnly,
                },
            };
        },
        answerValue () {
            if (this.answerValues) {
                return this.answerValues.find((value) => value.answerId === this.rule.answerId);
            }

            return this.rule.value;
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
        dateComponent () {
            switch (this.rule.time) {
                case 'withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'between':
                    return getAbsoluteDateRangeConfig(this.rule);
                default:
                    return false;
            }
        },
    },
    watch: {
        async survey (newSurvey) {
            const surveyItemsAggResponse = await runAggregationOnce(SurveyItemsAggregation, {
                surveyItemIds: newSurvey.surveyItemIds,
            });

            this.surveyQuestions = surveyItemsAggResponse
                .filter((item) => item.type !== SURVEY_QUESTION_TYPES.BASIC)
                .map((item) => ({
                    surveyQuestionId: item.id,
                    name: item.questionText,
                    surveyQuestionType: item.type,
                    answers: item.answers
                        ? item.answers.map((answer) => ({
                            value: answer.value,
                            name: answer.text,
                            answerId: answer.id,
                        }))
                        : [],
                }));

            // Fill in the gaps for rules created pre APP-134705
            if (this.rule.surveyId && !this.rule.guideId) {
                this.answerValues.filter((answerValue) => answerValue.value === this.rule.value).forEach((answerValue) => this.onAnswerChange({ value: answerValue }));
                this.$emit('input', { field: 'guideId', value: { guideId: this.survey.guideId } });
                this.$emit('input', { field: 'answers', value: { answers: this.answerValues } });
            }
        },
    },
    created () {
        this.timeOptions = getTimeOptions().survey;
    },
    methods: {
        onSurveyChange ({ value }) {
            if (value) {
                this.$emit('input', { field: 'surveyId', value });
                this.$emit('input', { field: 'guideId', value });
            }
        },
        onSurveyQuestionChange ({ value }) {
            if (value) {
                this.$emit('input', { field: 'surveyQuestionId', value });
                this.$emit('input', { field: 'surveyQuestionType', value });
                this.$emit('input', { field: 'answers', value });
            }
        },
        onAnswerChange ({ value }) {
            if (value) {
                this.$emit('input', { field: 'value', value });
                this.$emit('input', { field: 'answerId', value });
            }
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
